import React, { useState } from "react";

import { Text, Wizard, WizardStep } from "@performyard/py-components";
import {
	DEFAULT_REVIEW_CYCLE_DUE_TIME,
	DEFAULT_REVIEW_START_TIME,
	DEFAULT_TIMEZONE,
} from "@performyard/utils-isomorphic";

import type { CycleType } from "./Config";
import { ExitWizardModal } from "./components/ExitWizardModal";
import { WizardStepWrapper } from "./components/WizardStepWrapper";

import { useCompanySettings } from "../../../../api/hooks/defs/company";
import useUnsavedChanges from "../../../../hooks/useUnsavedChanges";

import { CycleSettingsStep, getSettingsDefaults, getSettingsSchema } from "./steps/CycleSettingsStep";

type ReviewCycleWizardProps = {
	cycleType: CycleType;
	onExit: () => void;
};

const CYCLE_TYPE_LABELS: Record<CycleType, string> = {
	manual: "Manual",
	automated: "Automated",
	group: "Group",
};

export const ReviewCycleWizard = ({ cycleType, onExit }: ReviewCycleWizardProps) => {
	const [showExitConfirm, setShowExitConfirm] = useState(false);
	const [suppressUnsavedChanges, setSuppressUnsavedChanges] = useState(false);
	const { setIsUnsafe, setIsSafe } = useUnsavedChanges({
		message: "You have unsaved changes. Are you sure you want to leave this page?",
		disabled: suppressUnsavedChanges,
	});

	const handleExitRequest = () => {
		setSuppressUnsavedChanges(true);
		setShowExitConfirm(true);
	};

	const handleExitConfirm = () => {
		setIsSafe();
		onExit();
	};

	const handleComplete = () => {
		setIsSafe();
		// Future tickets will implement actual cycle creation
	};

	const typeLabel = CYCLE_TYPE_LABELS[cycleType];
	const stepTitle = `Create a ${typeLabel} Review Cycle`;

	const { data: companySettings } = useCompanySettings();
	const companyTimezone = companySettings?.default_timezone ?? DEFAULT_TIMEZONE;
	const reviewSettings = companySettings?.functions?.reviews as
		| { default_review_start_time?: number; default_review_cycle_due_time?: number }
		| undefined;
	const defaultStartTime = reviewSettings?.default_review_start_time ?? DEFAULT_REVIEW_START_TIME;
	const defaultEndTime = reviewSettings?.default_review_cycle_due_time ?? DEFAULT_REVIEW_CYCLE_DUE_TIME;

	const settingsSchema = getSettingsSchema(cycleType, companyTimezone);
	const settingsDefaults = getSettingsDefaults(cycleType);

	return (
		<>
			<ExitWizardModal
				isOpen={showExitConfirm}
				onClose={() => {
					setShowExitConfirm(false);
					setSuppressUnsavedChanges(false);
				}}
				onConfirm={handleExitConfirm}
			/>
			<Wizard
				id="review-cycle-wizard"
				title="Creation Steps"
				onComplete={handleComplete}
				onExit={handleExitRequest}
				onStepChange={() => setIsUnsafe()}
			>
				<WizardStep
					id="basic-info"
					title="Cycle Settings"
					schema={settingsSchema}
					defaultValues={settingsDefaults}
				>
					{() => (
						<WizardStepWrapper title={stepTitle} subtitle="Cycle Settings">
							<CycleSettingsStep
								cycleType={cycleType}
								companyTimezone={companyTimezone}
								defaultStartTime={defaultStartTime}
								defaultEndTime={defaultEndTime}
							/>
						</WizardStepWrapper>
					)}
				</WizardStep>
				<WizardStep id="template" title="Review Template">
					{() => <WizardStepWrapper title={stepTitle} subtitle="Add Forms" />}
				</WizardStep>
				<WizardStep id="subjects" title="Select Subjects">
					{() => <WizardStepWrapper title={stepTitle} subtitle="Add Subjects" />}
				</WizardStep>
				<WizardStep id="review" title="Review & Create">
					{({ wizardData }) => (
						<WizardStepWrapper title={stepTitle} subtitle="Preview and Finalize">
							<div className="mt-4 rounded border border-solid border-neutral-3 bg-neutral-1 p-4">
								<Text
									size="paragraph-3"
									weight="semi-bold"
									color="neutral-9"
									dangerouslySetClassName="mb-2"
								>
									Summary
								</Text>
								<pre className="overflow-auto whitespace-pre-wrap text-sm text-neutral-7">
									{JSON.stringify({ cycleType, ...wizardData }, null, 2)}
								</pre>
							</div>
						</WizardStepWrapper>
					)}
				</WizardStep>
			</Wizard>
		</>
	);
};
